import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Route, Routes } from 'react-router';
import { PrismicDocument } from '@prismicio/client';
import { useFlag } from '@unleash/proxy-client-react';

import { AlertBanner } from '@api/types/prismic/alertBanner';
import { CookiesDialog } from '@components/CookiesModal';
import { createCookiesConfig } from '@components/CookiesModal/config';
import { GrantAccessOrRedirect } from '@components/GrantAccess/GrantAccessOrRedirect';
import { AccountLayout } from '@components/Layout/Account';
import { AppLayout } from '@components/Layout/App';
import { CommonLayout } from '@components/Layout/Common';
import { LoginLayout } from '@components/Layout/Login';
import { PrismicAlertsBanner } from '@components/PrismicAlertsBanner';
import { Private } from '@components/Private';
import { RedirectToToday } from '@components/RedirectToToday/RedirectToToday';
import { client } from '@config/prismic';
import { ROUTES } from '@config/routes';
import { PRISMIC_ALERTS_ENABLED } from '@config/unleashFlags';
import { useCookies } from '@hooks/useCookies';
import { useGTM } from '@hooks/useGTM';

import {
  Account,
  CguValidation,
  Home,
  Login,
  Meeting,
  MeetingsCalendar,
  NotFound,
  QuintePlus,
  Race,
  NonRunners,
  RacingEvents,
  Racecards,
  Publications,
  Proposal,
} from './features';
import './style/layout/print.scss';

const App = () => {
  const intl = useIntl();
  const cookiesConfig = createCookiesConfig(intl);
  const isPrismicAlertsEnabled = useFlag(PRISMIC_ALERTS_ENABLED);
  const [alertDocument, setAlertDocument] = useState<PrismicDocument<AlertBanner> | undefined>(
    undefined
  );

  useEffect(() => {
    if (!isPrismicAlertsEnabled) {
      return;
    }

    client
      .getByUID<PrismicDocument<AlertBanner>>('infocentre_alerts', 'global')
      .then(setAlertDocument)
      .catch(() => setAlertDocument(undefined));
  }, [isPrismicAlertsEnabled]);

  useGTM();
  useCookies(cookiesConfig);

  const alertPrismic = alertDocument?.data;

  return (
    <>
      <PrismicAlertsBanner alert={alertPrismic} />
      <CookiesDialog cookiesConfig={cookiesConfig} />
      <Routes>
        <Route path={ROUTES.HOME} element={<CommonLayout />}>
          <Route index element={<RedirectToToday basePath={ROUTES.DASHBOARD_BASE} />} />
          <Route
            path={ROUTES.DASHBOARD_BASE}
            element={<RedirectToToday basePath={ROUTES.DASHBOARD_BASE} />}
          />
          <Route
            path={ROUTES.DASHBOARD}
            element={
              <Private>
                <GrantAccessOrRedirect resourceId="app.route.home">
                  <Home />
                </GrantAccessOrRedirect>
              </Private>
            }
          />
          <Route path={ROUTES.LOGIN} element={<LoginLayout />}>
            <Route index element={<Login />} />
          </Route>
          <Route path={ROUTES.CGU_VALIDATION} element={<LoginLayout />}>
            <Route
              index
              element={
                <Private>
                  <CguValidation />
                </Private>
              }
            />
          </Route>

          <Route
            path={ROUTES.RACECARDS_BASE}
            element={<RedirectToToday basePath={ROUTES.RACECARDS_BASE} />}
          />
          <Route
            path={ROUTES.QUINTE_BASE}
            element={<RedirectToToday basePath={ROUTES.QUINTE_BASE} />}
          />
          <Route
            path={ROUTES.NON_RUNNERS_BASE}
            element={<RedirectToToday basePath={ROUTES.NON_RUNNERS_BASE} />}
          />
          <Route
            path={ROUTES.RACING_EVENTS_BASE}
            element={<RedirectToToday basePath={ROUTES.RACING_EVENTS_BASE} />}
          />
          <Route
            path={ROUTES.PUBLICATIONS_BASE}
            element={<RedirectToToday basePath={ROUTES.PUBLICATIONS_BASE} />}
          />

          <Route path={ROUTES.MEETINGS_CALENDAR} element={<AppLayout />}>
            <Route
              index
              element={
                <Private>
                  <GrantAccessOrRedirect resourceId="app.route.meetingsCalendar">
                    <MeetingsCalendar />
                  </GrantAccessOrRedirect>
                </Private>
              }
            />
          </Route>
          <Route path={ROUTES.RACECARDS} element={<AppLayout />}>
            <Route
              index
              element={
                <Private>
                  <GrantAccessOrRedirect resourceId="app.route.racecards">
                    <Racecards />
                  </GrantAccessOrRedirect>
                </Private>
              }
            />
            <Route
              path={ROUTES.MEETINGS}
              element={
                <Private>
                  <GrantAccessOrRedirect resourceId="app.route.meetings">
                    <Meeting />
                  </GrantAccessOrRedirect>
                </Private>
              }
            />
            <Route
              path={ROUTES.RACES}
              element={
                <Private>
                  <GrantAccessOrRedirect resourceId="app.route.races">
                    <Race />
                  </GrantAccessOrRedirect>
                </Private>
              }
            />
          </Route>
          <Route path={ROUTES.QUINTE} element={<AppLayout />}>
            <Route
              index
              element={
                <Private>
                  <GrantAccessOrRedirect resourceId="app.route.quinte">
                    <QuintePlus />
                  </GrantAccessOrRedirect>
                </Private>
              }
            />
          </Route>
          <Route path={ROUTES.NON_RUNNERS} element={<AppLayout />}>
            <Route
              index
              element={
                <Private>
                  <GrantAccessOrRedirect resourceId="app.route.nonRunners">
                    <NonRunners />
                  </GrantAccessOrRedirect>
                </Private>
              }
            />
          </Route>
          <Route path={ROUTES.RACING_EVENTS} element={<AppLayout />}>
            <Route
              index
              element={
                <Private>
                  <GrantAccessOrRedirect resourceId="app.route.racingEvents">
                    <RacingEvents />
                  </GrantAccessOrRedirect>
                </Private>
              }
            />
          </Route>
          <Route path={ROUTES.PUBLICATIONS} element={<AppLayout />}>
            <Route
              index
              element={
                <Private>
                  <GrantAccessOrRedirect resourceId="app.route.publications">
                    <Publications />
                  </GrantAccessOrRedirect>
                </Private>
              }
            />
          </Route>
          <Route path={ROUTES.PROPOSAL} element={<AppLayout />}>
            <Route
              index
              element={
                <Private>
                  <GrantAccessOrRedirect resourceId="app.route.proposal">
                    <Proposal />
                  </GrantAccessOrRedirect>
                </Private>
              }
            />
          </Route>
          <Route path={ROUTES.ACCOUNT} element={<AccountLayout />}>
            <Route
              index
              element={
                <Private>
                  <GrantAccessOrRedirect resourceId="app.route.account">
                    <Account />
                  </GrantAccessOrRedirect>
                </Private>
              }
            />
          </Route>

          <Route
            path="*"
            element={
              <Private>
                <NotFound />
              </Private>
            }
          />
        </Route>
      </Routes>
    </>
  );
};

export { App };
